import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import CircularProgress from '@mui/material/CircularProgress'
import LogoutIcon from '@mui/icons-material/Logout'
import { getImpersonation, onImpersonationChange, exitImpersonation, ImpersonationFlag } from '../../auth/impersonation'

// Always-mounted red bar shown while a staffer is logged in AS a student. It
// sits above everything (including the student portal it renders into) so the
// staffer can never lose track of the impersonation or the way out.
export default function ImpersonationBanner() {
  const navigate = useNavigate()
  const [flag, setFlag]   = useState<ImpersonationFlag | null>(getImpersonation())
  const [busy, setBusy]   = useState(false)

  useEffect(() => onImpersonationChange(() => setFlag(getImpersonation())), [])

  if (!flag) return null

  async function exit() {
    setBusy(true)
    try {
      await exitImpersonation()
      navigate('/students')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Box
      sx={{
        position: 'sticky', top: 0, zIndex: (t) => t.zIndex.appBar + 2,
        bgcolor: 'error.main', color: 'error.contrastText',
        px: 2, py: 0.75, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
        boxShadow: 2,
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        You are logged in AS {flag.studentName || 'this student'}
      </Typography>
      <Typography variant="caption" sx={{ opacity: 0.9, flexGrow: 1, minWidth: 160 }}>
        Actions you take here are real and affect their account. Exit to return to your own login.
      </Typography>
      <Button
        size="small" variant="contained" color="inherit" disabled={busy}
        onClick={exit}
        startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <LogoutIcon fontSize="small" />}
        sx={{ color: 'error.main', bgcolor: 'common.white', '&:hover': { bgcolor: 'grey.100' } }}
      >
        {busy ? 'Exiting…' : 'Exit — back to me'}
      </Button>
    </Box>
  )
}
